import {
    createContext,
    FC,
    PropsWithChildren,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

import { isFetchResultSuccessful } from '@/entities/FetchResult'
import { StreamTokenClaims } from '@/entities/StreamToken/StreamToken'
import { getStreamTokenClaims, isStreamTokenExpired } from '@/entities/StreamToken/StreamTokenHelpers'
import { getStreamTokenApiCall } from '@/entities/StreamToken/StreamTokenService'

interface StreamCredentials {
    token: string
    claims: StreamTokenClaims
}

type StreamCredentialsContextValue =
    | { status: 'loading' | 'unavailable' }
    | { status: 'ready', userId: string, tokenProvider: () => Promise<string> }

const StreamCredentialsContext = createContext<StreamCredentialsContextValue | null>(null)

const fetchStreamCredentials = async (): Promise<StreamCredentials | undefined> => {
    const result = await getStreamTokenApiCall()

    if (! isFetchResultSuccessful(result) || ! result.data) {
        return undefined
    }

    const claims = getStreamTokenClaims(result.data)

    if (! claims) {
        console.error('[fetchStreamCredentials] Stream token does not contain a user_id claim')

        return undefined
    }

    return { token: result.data, claims }
}

/**
 * Fetches the current user's Stream token once for both the Chat and Feeds connections. A `null`
 * token means the user is not meant to be connected to Stream, in which case neither connects.
 */
export const StreamCredentialsProvider: FC<PropsWithChildren> = ({ children }): ReactElement => {
    const credentialsRef = useRef<StreamCredentials | undefined>(undefined)
    const [userId, setUserId] = useState<string>()
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        let mounted = true

        fetchStreamCredentials()
            .then(credentials => {
                if (! mounted) {
                    return
                }

                credentialsRef.current = credentials
                setUserId(credentials?.claims.userId)
            })
            .catch(error => console.error(error))
            .finally(() => mounted && setIsLoading(false))

        return () => {
            mounted = false
        }
    }, [])

    // Stream calls this on connect and whenever the token expires; reuse the cached token until then.
    const tokenProvider = useCallback(async (): Promise<string> => {
        const cached = credentialsRef.current

        if (cached && ! isStreamTokenExpired(cached.claims)) {
            return cached.token
        }

        const credentials = await fetchStreamCredentials()

        if (! credentials) {
            throw new Error('Could not refresh Stream token')
        }

        credentialsRef.current = credentials

        return credentials.token
    }, [])

    const value = useMemo((): StreamCredentialsContextValue => {
        if (isLoading) {
            return { status: 'loading' }
        }

        return userId ? { status: 'ready', userId, tokenProvider } : { status: 'unavailable' }
    }, [isLoading, userId, tokenProvider])

    return (
        <StreamCredentialsContext.Provider value={value}>
            {children}
        </StreamCredentialsContext.Provider>
    )
}

export const useStreamCredentials = (): StreamCredentialsContextValue => {
    const context = useContext(StreamCredentialsContext)

    if (! context) {
        throw new Error('useStreamCredentials must be used within a StreamCredentialsProvider')
    }

    return context
}
